import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

const settingSchema = yup.object().shape({
  customInstructions: yup.string().optional().default(''),
});

type SettingDTO = yup.InferType<typeof settingSchema>;

function AiTicketScanningSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);

  const { control, handleSubmit, reset } = useForm<SettingDTO>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      customInstructions: userStore.userCompany.aiTicketScanningCustomInstructions ?? '',
    },
  });

  const onSaveInstructions = handleSubmit(async (data) => {
    setIsSaving(true);

    try {
      await updateCompany({
        company: {
          id: userStore.userCompany.id,
          aiTicketScanningCustomInstructions: data.customInstructions || null,
        },
      });

      setIsEditingInstructions(false);

      toasterStore.push(
        alert(t('administration.configuration.configuration_saved'), AlertTypes.success),
      );
    } catch {
      // Keep form values so the user can retry
    } finally {
      setIsSaving(false);
    }
  });

  const onCancelInstructions = () => {
    reset({
      customInstructions: userStore.userCompany.aiTicketScanningCustomInstructions ?? '',
    });
    setIsEditingInstructions(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('administration.configuration.settings.ai_ticket_scanning.setting_title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Box>
          <Typography variant="subtitle2">
            {t(
              'administration.configuration.settings.ai_ticket_scanning.custom_instructions_title',
            )}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(
              'administration.configuration.settings.ai_ticket_scanning.custom_instructions_description',
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditingInstructions ? (
            <>
              <SmallButton
                color="secondary"
                disabled={isSaving}
                onClick={onCancelInstructions}
                variant="outlined"
              >
                {t('actions.cancel')}
              </SmallButton>

              <SmallLoadingButton
                color="brandV2Yellow"
                loading={isSaving}
                onClick={onSaveInstructions}
              >
                {t('actions.save')}
              </SmallLoadingButton>
            </>
          ) : (
            <SmallButton
              color="brandV2Yellow"
              disabled={isSaving}
              onClick={() => setIsEditingInstructions(true)}
            >
              {t('actions.edit')}
            </SmallButton>
          )}
        </Box>
      </Box>

      <TextFormField
        control={control}
        disabled={!isEditingInstructions || isSaving}
        multiline
        name="customInstructions"
        placeholder={t(
          'administration.configuration.settings.ai_ticket_scanning.custom_instructions_placeholder',
        )}
        rows={4}
      />
    </Box>
  );
}

export default {
  component: AiTicketScanningSetting,
  canAccess: (userStore: UserStore) => {
    return userStore.getPermissions().canEditCompany;
  },
};
